import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from "react";
import { getOAuthState, getStoredInviteCode, clearInviteCode } from "@/utils/oauth";
import { getAttributionPayload, ensureCountry } from '@/utils/attribution';
import { setAuthToken, getAuthToken } from '@/utils/appsettings';
import { gtmPushUserId, gtmPushSignUp } from '@/utils/gtm';
import { toast } from '@/hooks/use-toast';
import { onCreditUpdate } from '@/utils/creditEvents';

/**
 * Thrown by login() when the backend returns 202 "mfa_required".
 * The Login page catches this and shows the TOTP input step.
 */
export class MfaRequiredError extends Error {
  readonly mfaToken: string;

  constructor(mfaToken: string) {
    super("A second factor is required to complete sign-in.");
    this.name = "MfaRequiredError";
    this.mfaToken = mfaToken;
  }
}

/**
 * Thrown by loginWithOAuth when the backend returns 409 "account_exists_password".
 * Carry enough data so the OAuthCallback page can present the linking UI without
 * making another round-trip to the server.
 */
export class OAuthLinkRequiredError extends Error {
  readonly pendingLinkToken: string;
  readonly email: string;
  readonly provider: string;

  constructor(pendingLinkToken: string, email: string, provider: string) {
    super(
      "An account with this email already exists. " +
      "Please enter your password to connect your account."
    );
    this.name = "OAuthLinkRequiredError";
    this.pendingLinkToken = pendingLinkToken;
    this.email = email;
    this.provider = provider;
  }
}

// OAuth Configuration (same as in oauth.ts)
const _origin = typeof window !== 'undefined' ? window.location.origin : 'https://joylo.ai';
const OAUTH_CONFIG = {
  google: {
    redirectUri: `${_origin}/auth/callback/google`,
  },
  github: {
    redirectUri: `${_origin}/auth/callback/github`,
  },
  facebook: {
    redirectUri: `${_origin}/auth/callback/facebook`,
  },
  apple: {
    redirectUri: `${_origin}/auth/callback/apple`,
  },
  microsoft: {
    redirectUri: `${_origin}/auth/callback/microsoft`,
  }
};

// New interfaces for roles and permissions
interface Role {
  id: number;
  name: string;
  display_name: string;
  description?: string;
}

interface Subscription {
  plan_id: string;
  plan_name: string;
  status: string;
  billing_cycle: string;
  current_period_end: string;
  llm_credits_remaining: number;
  architect_hours_remaining: number;
  projects_remaining: number;
}

// Updated User interface with roles and permissions
interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider?: string;
  verified?: boolean;
  referral_source_captured?: boolean;
  created_at?: string;
  last_login?: string;
  roles?: Role[]; // Added roles support
  permissions?: string[]; // Added permissions support
  subscription?: Subscription; // Added subscription support
  is_influencer?: boolean; // Gates the influencer dashboard nav item
}

interface AuthResponse {
  user: User;
  token: string;
  refresh_token?: string;
  expires_in?: number;
  is_new_user?: boolean;
}

// New interface for the enhanced verify response
interface VerifyResponse {
  valid: boolean;
  user: User;
  roles: Role[];
  permissions: string[];
  subscription?: Subscription;
  is_influencer?: boolean;
  token_info: {
    expires_at: string;
    issued_at: string;
    token_type: string;
  };
}

// Updated AuthContextType with new function
interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Complete a 2FA challenge after login returned 202 mfa_required. */
  completeMfaChallenge: (mfaToken: string, code: string) => Promise<void>;
  loginWithOAuth: (provider: string, code: string, state: string) => Promise<void>;
  /** Verify password and complete an OAuth→password-account link. */
  linkOAuthWithPassword: (pendingLinkToken: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUserData: () => Promise<void>; // New function to refresh user data with roles
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// In-memory expiry tracking (replaces localStorage for token expiry)
let _tokenExpiresAt: number | null = null;

// Cross-tab auth sync: tabs broadcast login/logout so already-open tabs stay in sync.
// httpOnly cookie remains the source of truth — we only signal "re-read the session".
const AUTH_SYNC_KEY = 'joylo_auth_sync';
type AuthSyncEvent = { type: 'login' | 'logout'; ts: number };

// Lazily created, browser-only channel. Guarded so SSR/test environments stay inert.
const authChannel: BroadcastChannel | null = (() => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(AUTH_SYNC_KEY);
  } catch {
    return null;
  }
})();

// Broadcast an auth change to other tabs. Uses BroadcastChannel when available,
// and always writes a localStorage marker as a Safari/older-browser fallback.
const broadcastAuthChange = (type: AuthSyncEvent['type'], ts: number) => {
  const event: AuthSyncEvent = { type, ts };
  try {
    authChannel?.postMessage(event);
  } catch {
    // ignore channel post failures
  }
  try {
    // Value must change each time so the storage event fires in every other tab.
    localStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(event));
  } catch {
    // ignore storage write failures (private mode, quota)
  }
};

// Three-way /auth/verify result: only 'invalid' may end the session, 'unknown' covers 5xx and network errors.
type VerifyOutcome = 'valid' | 'invalid' | 'unknown';
const isInvalidSessionStatus = (status: number) => status === 401 || status === 403;
const VERIFY_RETRY_BASE_MS = 2000;
const VERIFY_RETRY_MAX_MS = 30000;
const VERIFY_RETRY_MAX_ATTEMPTS = 5;
const VERIFY_RETRY_JITTER_MS = 1000;

const clearOAuthState = () => {
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('oauth_provider');
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('oauth_provider');
  sessionStorage.removeItem('oauth_state_backup');
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Track ongoing OAuth requests to prevent duplicates
  const oauthRequestsRef = useRef<Set<string>>(new Set());

  // Cross-tab sync: timestamp of the last auth event this tab originated or applied,
  // used to ignore stale/out-of-order broadcasts. Plus an in-flight guard so concurrent
  // sync events (BroadcastChannel + storage fallback) coalesce into one session re-read.
  const lastSyncTsRef = useRef<number>(0);
  const syncRestoreInFlightRef = useRef<boolean>(false);

  // Store auth data in memory only — no localStorage
  const storeAuthData = (authResponse: AuthResponse) => {
    if (authResponse.expires_in) {
      _tokenExpiresAt = Date.now() + authResponse.expires_in * 1000;
    }
    setAuthToken(authResponse.token);
    setUser(authResponse.user);
    setIsAuthenticated(true);
    lastSyncTsRef.current = Date.now();
    broadcastAuthChange('login', lastSyncTsRef.current);
  };

  // Enhanced verify token function that updates user data with roles
  const verifyToken = async (token: string): Promise<VerifyOutcome> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const err = await response.json();
            if (typeof err.detail === 'string' && err.detail.includes('deactivated')) {
              toast({
                variant: 'destructive',
                title: 'Account Deactivated',
                description: err.detail,
              });
            }
          } catch {
            // ignore parse failure — fall through to the generic invalid-session path
          }
        }
        // Server signals a dead session only via 401/403; 5xx and gateway errors during pod restarts are transient.
        return isInvalidSessionStatus(response.status) ? 'invalid' : 'unknown';
      }

      // Try to parse the new format response
      try {
        const verifyData: VerifyResponse = await response.json();

        if (verifyData.valid) {
          const updatedUser: User = {
            ...verifyData.user,
            roles: verifyData.roles,
            permissions: verifyData.permissions,
            subscription: verifyData.subscription,
            is_influencer: verifyData.is_influencer
          };
          setUser(updatedUser);

          return 'valid';
        }

        return 'invalid';
      } catch (parseError) {
        // A 200 with an unparseable body is usually a gateway/maintenance page, so retry rather than trust it.
        return 'unknown';
      }
    } catch (error) {
      return 'unknown';
    }
  };

  // Backoff retry for transient verify failures; reset on success and cancelled on sign-out or unmount.
  const verifyRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const verifyRetryAttemptRef = useRef(0);

  const refreshUserData = async () => {
    const token = getAuthToken();
    if (!token) return;
    const outcome = await verifyToken(token);
    // A late reply for a token that was since replaced (re-login, account switch) must not end the new session.
    if (getAuthToken() !== token) return;
    if (outcome === 'invalid') {
      await logout();
      return;
    }
    if (outcome === 'valid') {
      verifyRetryAttemptRef.current = 0;
      return;
    }
    // Unknown: keep the session and current user; logging out here would revoke a valid JWT in every tab.
    if (verifyRetryTimerRef.current) return;
    if (verifyRetryAttemptRef.current >= VERIFY_RETRY_MAX_ATTEMPTS) {
      // Budget spent: stop this loop but let the next externally triggered refresh start a fresh one.
      verifyRetryAttemptRef.current = 0;
      return;
    }
    const backoff = Math.min(VERIFY_RETRY_BASE_MS * 2 ** verifyRetryAttemptRef.current, VERIFY_RETRY_MAX_MS);
    // Jitter keeps many open tabs from hitting a recovering API in lockstep.
    const delay = backoff + Math.floor(Math.random() * VERIFY_RETRY_JITTER_MS);
    verifyRetryAttemptRef.current += 1;
    verifyRetryTimerRef.current = setTimeout(() => {
      verifyRetryTimerRef.current = null;
      refreshUserDataRef.current().catch(() => {});
    }, delay);
  };

  // Restore session from the httpOnly cookie — no localStorage involved.
  // Reused both at mount and when another tab signals a login (cross-tab sync).
  const restoreSession = useCallback(async () => {
    try {
      // Clean up any legacy localStorage auth values from before this fix
      localStorage.removeItem('joylo_auth_token');
      localStorage.removeItem('joylo_user_data');
      localStorage.removeItem('joylo_refresh_token');
      localStorage.removeItem('joylo_token_expires_at');
      localStorage.removeItem('joylo_new_user');

      const response = await fetch(
        `${import.meta.env.VITE_BASE_API_URL}/auth/session`,
        { method: 'GET', credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.valid && data.token) {
          if (data.expires_in) {
            _tokenExpiresAt = Date.now() + data.expires_in * 1000;
          }
          setAuthToken(data.token);
          const sessionUser: User = {
            ...data.user,
            roles: data.roles,
            permissions: data.permissions,
            subscription: data.subscription,
            is_influencer: data.is_influencer,
          };
          setUser(sessionUser);
          setIsAuthenticated(true);
        }
      }
    } catch {
      // No valid session — stay logged out
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Cross-tab auth sync: react to login/logout broadcast by other tabs.
  // On a peer login we re-read the shared cookie; on a peer logout we clear in-memory state.
  useEffect(() => {
    const applySync = (event: AuthSyncEvent | null) => {
      // Validate shape; drop the duplicate delivery (BroadcastChannel + storage fallback
      // deliver the same event). Exact-ts match dedups without rejecting rapid distinct events.
      if (!event || (event.type !== 'login' && event.type !== 'logout')) return;
      if (typeof event.ts !== 'number' || event.ts === lastSyncTsRef.current) return;
      lastSyncTsRef.current = event.ts;

      if (event.type === 'login') {
        // Re-read the shared cookie only if this tab isn't already authenticated.
        // In-flight guard coalesces the BroadcastChannel + storage events into one fetch.
        if (!getAuthToken() && !syncRestoreInFlightRef.current) {
          syncRestoreInFlightRef.current = true;
          restoreSession().finally(() => {
            syncRestoreInFlightRef.current = false;
          });
        }
      } else if (getAuthToken()) {
        // Peer already invalidated the server session — just drop local state here.
        _tokenExpiresAt = null;
        setAuthToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    const onChannelMessage = (e: MessageEvent) => applySync(e.data as AuthSyncEvent);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_SYNC_KEY || !e.newValue) return;
      try {
        applySync(JSON.parse(e.newValue) as AuthSyncEvent);
      } catch {
        // ignore malformed payloads
      }
    };

    authChannel?.addEventListener('message', onChannelMessage);
    window.addEventListener('storage', onStorage);
    return () => {
      authChannel?.removeEventListener('message', onChannelMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [restoreSession]);

  // Stop pending verify retries once signed out (here or in another tab) and on unmount.
  useEffect(() => {
    const cancel = () => {
      if (verifyRetryTimerRef.current) clearTimeout(verifyRetryTimerRef.current);
      verifyRetryTimerRef.current = null;
      verifyRetryAttemptRef.current = 0;
    };
    if (!isAuthenticated) cancel();
    return cancel;
  }, [isAuthenticated]);

  // Plan gates (usePlanFeatures) read user.subscription, so a mid-session upgrade must re-read it or open projects stay locked until reload.
  const refreshUserDataRef = useRef(refreshUserData);
  refreshUserDataRef.current = refreshUserData;
  const subscriptionRefreshInFlightRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    // Unsourced emits come from BillingManage subscription changes (cancel/reactivate); sourced ones like log-stream are credit-only.
    return onCreditUpdate((source) => {
      if (source !== 'billing-success' && source !== undefined) return;
      if (subscriptionRefreshInFlightRef.current) return;
      subscriptionRefreshInFlightRef.current = true;
      refreshUserDataRef.current()
        .catch(() => {})
        .finally(() => {
          subscriptionRefreshInFlightRef.current = false;
        });
    });
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      if (!_tokenExpiresAt) return;
      if (Date.now() >= _tokenExpiresAt - 60000) {
        await logout();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Regular email/password login (enhanced with role fetching)
  const login = async (email: string, password: string) => {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }

    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      // 202 means 2FA is required — throw MfaRequiredError so the page can show the TOTP step
      if (response.status === 202) {
        const data = await response.json();
        if (data.mfa_required && data.mfa_token) {
          throw new MfaRequiredError(data.mfa_token);
        }
      }

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.message || error.detail || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        const error: any = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const authResponse: AuthResponse = await response.json();

      if (!authResponse.token) {
        throw new Error('No authentication token received');
      }

      storeAuthData(authResponse);
      gtmPushUserId(authResponse.user.id);

      // Refresh user data to get roles and permissions (don't await to avoid blocking)
      setTimeout(() => {
        refreshUserData().catch(() => {
          // Silent failure - user can still proceed even if role fetch fails
        });
      }, 100);

    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Complete a 2FA login challenge (step 2 after login returned 202)
  const completeMfaChallenge = async (mfaToken: string, code: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/2fa/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfa_token: mfaToken, code }),
      });

      if (!response.ok) {
        let errorMessage = 'Invalid code';
        try {
          const err = await response.json();
          errorMessage = err.detail || err.message || errorMessage;
        } catch { /* ignore */ }
        const error: any = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const authResponse: AuthResponse = await response.json();
      if (!authResponse.token) throw new Error('No authentication token received');
      storeAuthData(authResponse);
      gtmPushUserId(authResponse.user.id);
      setTimeout(() => { refreshUserData().catch(() => {}); }, 100);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // OAuth login with robust state handling and duplicate call protection (enhanced with role fetching)
  const loginWithOAuth = async (provider: string, code: string, state: string) => {
    // Create a unique request key to prevent duplicate calls
    const requestKey = `${provider}-${code}-${state}`;

    // Check if this exact request is already in progress
    if (oauthRequestsRef.current.has(requestKey)) {
      throw new Error('OAuth request already in progress');
    }

    // Add to ongoing requests
    oauthRequestsRef.current.add(requestKey);

    setLoading(true);

    try {
      // Get stored values with robust fallback
      const stored = getOAuthState();

      // Enhanced state verification with development bypass
      let stateVerified = false;

      if (!stored.state) {
        if (import.meta.env.DEV) {
          stateVerified = true; // Allow in development
        } else {
          throw new Error('No stored OAuth state found. This can happen if:\n1. You opened multiple browser tabs during login\n2. You clicked the back button during OAuth\n3. The browser cleared localStorage\n\nPlease try logging in again.');
        }
      } else if (!state) {
        throw new Error('No state parameter received from OAuth provider');
      } else if (state !== stored.state) {
        if (import.meta.env.DEV) {
          stateVerified = true; // Allow in development
        } else {
          throw new Error('Invalid state parameter - possible CSRF attack. Please try logging in again.');
        }
      } else {
        stateVerified = true;
      }

      if (!stateVerified) {
        throw new Error('OAuth state verification failed');
      }

      // Clean up stored state
      clearOAuthState();

      // Send code to backend for token exchange and profile save
      // Same bounded country fill as the password signup path
      await ensureCountry();

      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: "include",
        body: JSON.stringify({
          provider,
          code,
          redirect_uri: OAUTH_CONFIG[provider as keyof typeof OAUTH_CONFIG].redirectUri,
          invite_code: getStoredInviteCode() || undefined,
          attribution: getAttributionPayload(),
        }),
      });

      // 202 means 2FA is required — same as the password login flow
      if (response.status === 202) {
        const data = await response.json();
        if (data.mfa_required && data.mfa_token) {
          throw new MfaRequiredError(data.mfa_token);
        }
      }

      if (!response.ok) {
        // Parse the body once — it can only be consumed once.
        let body: Record<string, any> = {};
        try {
          body = await response.json();
        } catch {
          // ignore parse failure; fall through to generic message
        }

        if (response.status === 409) {
          if (body.error_code === 'account_exists_password') {
            // The email belongs to an existing password account.
            // Throw a typed error so OAuthCallback can show the linking UI.
            throw new OAuthLinkRequiredError(
              body.pending_link_token,
              body.email,
              provider
            );
          }
          if (body.error_code === 'account_exists_oauth') {
            const existing = body.existing_provider as string | undefined;
            const providerName = existing
              ? existing.charAt(0).toUpperCase() + existing.slice(1)
              : 'another method';
            throw new Error(
              body.message ||
              `This email is already linked to ${providerName}. Please sign in with ${providerName} instead.`
            );
          }
        }

        const errorMessage =
          body.message || body.detail ||
          response.statusText ||
          `OAuth ${provider} authentication failed`;
        throw new Error(errorMessage);
      }

      const authResponse: AuthResponse = await response.json();

      if (!authResponse.token) {
        throw new Error('No authentication token received from OAuth');
      }

      if (!authResponse.user) {
        throw new Error('No user data received from OAuth');
      }

      storeAuthData(authResponse);
      gtmPushUserId(authResponse.user.id);
      if (authResponse.is_new_user) {
        gtmPushSignUp(provider);
        clearInviteCode();
      }

      // Refresh user data to get roles and permissions (don't await to avoid blocking)
      setTimeout(() => {
        refreshUserData().catch(() => {
          // Silent failure - user can still proceed even if role fetch fails
        });
      }, 100);

    } catch (error) {
      throw error;
    } finally {
      // Always remove from ongoing requests, regardless of success/failure
      oauthRequestsRef.current.delete(requestKey);
      setLoading(false);
    }
  };

  // Link an OAuth provider to an existing password account (called after 409 flow)
  const linkOAuthWithPassword = async (
    pendingLinkToken: string,
    password: string
  ) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_BASE_API_URL}/auth/oauth/link`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ pending_link_token: pendingLinkToken, password }),
        }
      );

      let body: Record<string, any> = {};
      try {
        body = await response.json();
      } catch {
        // ignore
      }

      if (!response.ok) {
        throw new Error(
          body.detail || body.message || 'Account linking failed. Please try again.'
        );
      }

      const authResponse = body as AuthResponse;
      if (!authResponse.token || !authResponse.user) {
        throw new Error('Invalid response from server.');
      }

      storeAuthData(authResponse);

      setTimeout(() => {
        refreshUserData().catch(() => {
          // Silent failure - user can still proceed even if role fetch fails
        });
      }, 100);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);

    try {
      const token = getAuthToken();
      if (token) {
        try {
          await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch {
          // ignore network errors on logout
        }
      }
    } catch {
    } finally {
      _tokenExpiresAt = null;
      clearOAuthState();
      localStorage.removeItem('oauth_redirect_after');
      localStorage.removeItem('post_auth_redirect');

      setAuthToken(null);
      oauthRequestsRef.current.clear();
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      lastSyncTsRef.current = Date.now();
      broadcastAuthChange('logout', lastSyncTsRef.current);
    }
  };

  const refreshToken = async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No token available');
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const authResponse: AuthResponse = await response.json();
      storeAuthData(authResponse);

      // Refresh user data to get latest roles and permissions (don't await to avoid blocking)
      setTimeout(() => {
        refreshUserData().catch(error => {

        });
      }, 100);

    } catch (error) {
      // If refresh fails, logout user
      logout();
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      login,
      completeMfaChallenge,
      loginWithOAuth,
      linkOAuthWithPassword,
      logout,
      refreshToken,
      refreshUserData
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Helper hook to get auth token (in-memory, no localStorage)
export function useAuthToken() {
  return getAuthToken();
}